import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { timeCreateSchema } from "../../../shared/types/time";
import { Modal } from "../components/modal";
import { TimeForm } from "../components/time/form";
import { FETCH_ERROR } from "../consts/fetch-error";
import { useConfirm } from "../providers/confirm";
import { emit } from "../services/event-listner";
import { createTime } from "../services/time";
import { useLocale } from "../stores/locale";
import { dateFormat } from "../utils/date-format";
import { firstSecondOfMinute } from "../utils/first-second-of-minute";
import { isSameDay } from "../utils/is-same-day";
import { isValidDate } from "../utils/is-valide-date";
import { getWeek } from "../utils/week";

export function TimeCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isValid, setIsValid] = useState(false);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const t = useLocale((state) => state.locale);

  const dateParam = searchParams.get("date");

  if (dateParam === null || (dateParam !== null && !isValidDate(dateParam))) {
    throw new Error("Invalid date");
  }

  const date = new Date(dateParam);
  const [saving, setSaving] = useState(false);

  function onHide() {
    navigate(`/time?date=${dateParam}`);
  }

  function onChange(time: TimeForm) {
    // Not valid if project or task is empty or toTime is set but not fromTime
    setIsValid(
      time.project.trim() !== "" &&
        time.task.trim() !== "" &&
        (!time.endTime.trim() || time.startTime.trim() !== ""),
    );
  }

  function onSubmit(time: TimeForm) {
    (async () => {
      setSaving(true);
      const now = firstSecondOfMinute(new Date());

      const fromTime =
        time.startTime.trim() === ""
          ? now
          : new Date(`${dateFormat(date)} ${time.startTime}`);

      const toTime = time.endTime.trim()
        ? new Date(`${dateFormat(date)} ${time.endTime}`)
        : undefined;

      if (fromTime > now && !toTime) {
        // Set fromTime to the previous day if it is before now
        fromTime.setDate(fromTime.getDate() - 1);
      }

      if (toTime && toTime < fromTime) {
        // Set toTime to the next day if it is before fromTime
        toTime.setDate(toTime.getDate() + 1);
      }

      if (
        !isSameDay(fromTime, date) &&
        !(await confirm({
          message: t.time.confirmStartDifferentDay,
          header: t.confirmation,
        }))
      ) {
        return;
      }

      try {
        await createTime(
          timeCreateSchema.parse({
            ...(time.project.trim() ? { project: time.project.trim() } : {}),
            ...(time.task.trim() ? { task: time.task.trim() } : {}),
            ...(time.description.trim()
              ? { description: time.description.trim() }
              : {}),
            from: fromTime,
            ...(toTime ? { to: toTime } : {}),
          }),
        );
      } catch (error) {
        if (error === FETCH_ERROR.UNAUTHORIZED) {
          emit("error", error);
          return;
        }
        throw error;
      }

      const weekDates = getWeek(date);

      // Invalidate time for date
      await queryClient.refetchQueries({
        queryKey: ["get-times", date],
      });

      // Invalidate time for week
      await queryClient.refetchQueries({
        queryKey: ["get-times", weekDates[0], weekDates[6]],
      });

      navigate(`/time?date=${dateParam}`);
    })();
  }

  return (
    <>
      <Modal onClose={onHide}>
        <TimeForm
          isValid={isValid}
          submitting={saving}
          onCancel={onHide}
          onChange={onChange}
          onSubmit={onSubmit}
        />
      </Modal>
    </>
  );
}
